import React, { useEffect, useState } from "react";
import { api, ApiError, type ReferralStats } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { shareText } from "../../downloadUtils";
import { loadRazorpayCheckout } from "../../razorpayCheckout";

const APP_URL = "https://toletbook.in";

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function ReferEarn({ onBack }: { onBack: () => void }) {
  const toast = useToast();
  const { t } = useLanguage();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [claiming, setClaiming] = useState(false);

  function load() {
    api.get("/api/referrals/mine").then(setStats).catch(() => {});
  }
  useEffect(() => {
    load();
    api.get("/api/config").then(({ razorpayKeyId }) => setRazorpayKeyId(razorpayKeyId)).catch(() => {});
  }, []);

  async function share() {
    if (!stats) return;
    setBusy(true);
    try {
      const url = `${APP_URL}/?ref=${stats.code}`;
      const message = t("refer.shareMessage", { code: stats.code });
      const result = await shareText(t("refer.title"), message, url);
      if (result === "copied") toast(t("refer.copiedToast"));
    } finally {
      setBusy(false);
    }
  }

  async function claimReward() {
    if (!stats || !agreed) return;
    setClaiming(true);
    try {
      await loadRazorpayCheckout();
      const claim = await api.post("/api/referrals/claim-reward");
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: claim.keyId,
          subscription_id: claim.subscriptionId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: t("refer.mandateDescription", { date: fmtDate(claim.trialEndsAt) }),
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      toast(t("refer.claimSuccessToast", { date: fmtDate(claim.trialEndsAt) }));
      setShowConsent(false);
      setAgreed(false);
      load();
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("refer.claimFailedToast"));
      else if (err instanceof ApiError) toast(err.message);
      // "cancelled" (user closed checkout) fails silently
    } finally {
      setClaiming(false);
    }
  }

  const STATUS_BADGE: Record<string, string> = { pending: "vacant", rewarded: "paid", referee_only: "pending" };
  const STATUS_LABEL: Record<string, string> = {
    pending: t("refer.statusPending"),
    rewarded: t("refer.statusRewarded"),
    referee_only: t("refer.statusCapped"),
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("refer.title")}</div>
      </div>

      <div className="summary-card" style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontSize: 22, fontWeight: 700 }}>{t("refer.heroTitle")}</div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>{t("refer.heroSub")}</div>
      </div>

      <div className="card" style={{ marginBottom: 18, textAlign: "center", padding: "22px 15px" }}>
        <div className="sub">{t("refer.yourCodeLabel")}</div>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontSize: 32, fontWeight: 700, letterSpacing: 3, margin: "8px 0 16px" }}>
          {stats?.code ?? "······"}
        </div>
        <button className="btn-solid" disabled={!stats || busy} onClick={share}>
          {t("refer.shareButton")}
        </button>
      </div>

      {stats && (
        <div className="chip-row" style={{ marginBottom: 18 }}>
          <div className="chip" style={{ cursor: "default" }}>{t("refer.statsReferred", { count: String(stats.referrals.length) })}</div>
          <div className="chip" style={{ cursor: "default" }}>{t("refer.statsRewarded", { count: String(stats.rewardedCount) })}</div>
          <div className="chip" style={{ cursor: "default" }}>{t("refer.statsRemaining", { count: String(stats.remaining) })}</div>
        </div>
      )}

      {stats && stats.freeMonthsOwed > 0 && (
        <div className="card" style={{ marginBottom: 18, border: "1.5px solid var(--brand-700)" }}>
          <b style={{ fontSize: 14 }}>
            🎁 {t("refer.claimBannerTitle", { count: String(stats.freeMonthsOwed) })}
          </b>
          <div className="sub" style={{ marginTop: 6, lineHeight: 1.5 }}>{t("refer.claimBannerSub")}</div>

          {!showConsent ? (
            <button className="btn-solid" style={{ marginTop: 12 }} onClick={() => setShowConsent(true)}>
              {t("refer.claimButton")}
            </button>
          ) : (
            <div style={{ marginTop: 12 }}>
              <div className="sub" style={{ lineHeight: 1.8 }}>
                • {t("refer.consentBullet1")}
                <br />
                • {t("refer.consentBullet2", { count: String(stats.freeMonthsOwed) })}
                <br />
                • {t("refer.consentBullet3")}
                <br />
                • {t("refer.consentBullet4")}
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "12px 0" }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
                <span className="sub">{t("refer.consentCheckbox")}</span>
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="btn-solid"
                  style={{ flex: 1 }}
                  disabled={!agreed || claiming || !razorpayKeyId}
                  onClick={claimReward}
                >
                  {t("refer.consentConfirmButton")}
                </button>
                <button
                  className="btn-outline"
                  style={{ flex: 1 }}
                  disabled={claiming}
                  onClick={() => {
                    setShowConsent(false);
                    setAgreed(false);
                  }}
                >
                  {t("refer.consentCancelButton")}
                </button>
              </div>
              {!razorpayKeyId && (
                <div className="sub" style={{ marginTop: 8 }}>{t("refer.noChargeNote")}</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <b style={{ fontSize: 13 }}>{t("refer.howItWorksTitle")}</b>
        <div className="sub" style={{ lineHeight: 1.8, marginTop: 8 }}>
          1. {t("refer.step1")}
          <br />
          2. {t("refer.step2")}
          <br />
          3. {t("refer.step3")}
        </div>
      </div>

      <div className="section-title">{t("refer.historyTitle")}</div>
      {!stats ? null : stats.referrals.length === 0 ? (
        <div className="card">
          <div className="sub">{t("refer.emptyState")}</div>
        </div>
      ) : (
        stats.referrals.map((r, i) => (
          <div className="card" key={i}>
            <div className="card-row">
              <div>
                <b>{r.refereeName}</b>
                <div className="sub">{fmtDate(r.createdAt)}</div>
              </div>
              <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
            </div>
          </div>
        ))
      )}
    </>
  );
}
